// src/components/Logs/LogsStats.jsx
import React, { useEffect } from "react";
import { useLogsStore } from "../../services/useLogsStore";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const LogsStats = () => {
  const { stats, loading, error, fetchStats } = useLogsStore();

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLogTypeColor = (type) => {
    const colors = {
      API_CALL: "#3498db",
      USER_ACTION: "#2ecc71",
      AUTOMATION_EXECUTION: "#f39c12",
      INTEGRATION_EVENT: "#9b59b6",
      ERROR: "#e74c3c",
      SECURITY_EVENT: "#e67e22",
    };
    return colors[type] || "#95a5a6";
  };

  if (loading && !stats) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div className="spinner">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px", color: "#e74c3c" }}>
        <p>Error: {error}</p>
        <button
          onClick={fetchStats}
          style={{
            padding: "8px 16px",
            background: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Prepare pie chart data
  const pieData = Object.entries(stats.logs_by_type || {}).map(([type, count]) => ({
    name: type.replace(/_/g, " "),
    value: count,
    color: getLogTypeColor(type),
  }));

  return (
    <div style={{ padding: "20px" }}>
      {/* Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Logs Card */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: "14px", color: "#7f8c8d", marginBottom: "8px" }}>
            Total Audit Logs
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2c3e50" }}>
            {stats.total_logs.toLocaleString()}
          </div>
          <div style={{ fontSize: "12px", color: "#95a5a6", marginTop: "8px" }}>
            All-time records
          </div>
        </div>

        {/* Recent Logs Card */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: "14px", color: "#7f8c8d", marginBottom: "8px" }}>
            Recent Activity
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", color: "#3498db" }}>
            {stats.recent_logs_24h.toLocaleString()}
          </div>
          <div style={{ fontSize: "12px", color: "#95a5a6", marginTop: "8px" }}>
            Last 24 hours
          </div>
        </div>

        {/* Log Types Count Card */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: "14px", color: "#7f8c8d", marginBottom: "8px" }}>
            Log Types
          </div>
          <div style={{ fontSize: "32px", fontWeight: "bold", color: "#9b59b6" }}>
            {Object.keys(stats.logs_by_type || {}).length}
          </div>
          <div style={{ fontSize: "12px", color: "#95a5a6", marginTop: "8px" }}>
            Active categories
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
        }}
      >
        {/* Log Types Distribution - Pie Chart */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px" }}>
            Log Types Distribution
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: "#95a5a6" }}>
              No data available
            </div>
          )}
        </div>

        {/* Log Types Breakdown - List */}
        <div
          style={{
            background: "white",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px" }}>
            Log Types Breakdown
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {Object.entries(stats.logs_by_type || {}).length > 0 ? (
              Object.entries(stats.logs_by_type || {})
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => {
                  const percentage = ((count / stats.total_logs) * 100).toFixed(1);
                  return (
                    <div
                      key={type}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px",
                        background: "#f8f9fa",
                        borderRadius: "6px",
                        borderLeft: `4px solid ${getLogTypeColor(type)}`,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: "500", fontSize: "14px" }}>
                          {type.replace(/_/g, " ")}
                        </div>
                        <div style={{ fontSize: "12px", color: "#7f8c8d", marginTop: "2px" }}>
                          {percentage}% of total
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: getLogTypeColor(type),
                        }}
                      >
                        {count.toLocaleString()}
                      </div>
                    </div>
                  );
                })
            ) : (
              <div style={{ padding: "20px", textAlign: "center", color: "#95a5a6" }}>
                No log types data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div
        style={{
          marginTop: "20px",
          padding: "15px",
          background: "#ecf0f1",
          borderRadius: "6px",
          fontSize: "14px",
          color: "#7f8c8d",
        }}
      >
        <strong>Note:</strong> Statistics are calculated based on all audit logs in your workspace.
        Logs older than the configured retention period may be automatically deleted.
      </div>
    </div>
  );
};

export default LogsStats;
